import type { Context, Ref } from 'react';
import { useCallback, useLayoutEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { ContextValue, SlotProps } from 'react-aria-components/slots';
import { useSlottedContext } from 'react-aria-components/slots';
import { mergeProps } from 'react-aria/mergeProps';
import { useHref as useRouterHref } from 'react-router';
import { mergeRefs } from '@react-aria/utils';
import type { Href } from '@react-types/shared';

type ImageLoadingStatus = 'idle' | 'loading' | 'loaded' | 'error';

const getMediaServerSnapshot = () => false;

const useMedia = (media: string) => {
	const subscribe = useCallback(
		(onStoreChange: () => void) => {
			if (typeof window === 'undefined') {
				return () => undefined;
			}
			const mediaQueryList = window.matchMedia(media);
			mediaQueryList.addEventListener('change', onStoreChange);
			return () => {
				mediaQueryList.removeEventListener('change', onStoreChange);
			};
		},
		[media],
	);
	const getSnapshot = useCallback(
		() => (typeof window === 'undefined' ? false : window.matchMedia(media).matches),
		[media],
	);
	return useSyncExternalStore(subscribe, getSnapshot, getMediaServerSnapshot);
};

const ABSOLUTE_URL_REGEX = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

// https://github.com/remix-run/react-router/blob/main/packages/react-router/lib/dom/lib.tsx#L577-L585
const useHref = (href: Href) => {
	let absoluteHref: string | undefined;
	if (typeof href === 'string' && ABSOLUTE_URL_REGEX.test(href)) {
		absoluteHref = href;
	}
	const routerHref = useRouterHref(href);
	return absoluteHref || routerHref;
};

const useImageLoadingStatus = (src?: string) => {
	const [state, setState] = useState<{ src?: string; status: ImageLoadingStatus }>({
		src,
		status: src ? 'loading' : 'error',
	});

	if (src !== state.src) {
		setState({ src, status: src ? 'loading' : 'error' });
	}

	useLayoutEffect(() => {
		if (!src) {
			return;
		}

		let isMounted = true;
		const image = new window.Image();
		const updateStatus = (status: ImageLoadingStatus) => () => {
			if (!isMounted) {
				return;
			}
			setState({ src, status });
		};

		image.addEventListener('load', updateStatus('loaded'));
		image.addEventListener('error', updateStatus('error'));
		image.src = src;

		return () => {
			isMounted = false;
		};
	}, [src]);

	return src === state.src ? state.status : src ? 'loading' : 'error';
};

const useLPContextProps = <T, U extends SlotProps, E>(
	props: T & SlotProps,
	ref: Ref<E> | undefined,
	context: Context<ContextValue<U, E>>,
): [T, Ref<E | null>] => {
	const ctx = useSlottedContext(context, props.slot) || {};
	// @ts-expect-error -- `ctx` is `U | {}`; the `{}` branch has no `ref` property to destructure
	const { ref: contextRef, ...contextProps } = ctx;
	const mergedRef = useMemo(() => mergeRefs(ref, contextRef), [ref, contextRef]);
	const mergedProps = mergeProps(contextProps, props) as unknown as T;

	return [mergedProps, mergedRef];
};

export { useHref, useImageLoadingStatus, useLPContextProps, useMedia };
